import {memo, useEffect} from 'react'
import * as React from 'react'

import {getLastSuccessfulBuildLocator, getSingleBuildArg} from '../../../../../rest/builds'
import {getBuild, makeBuildBranchSelector} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import {defaultBranch, stringifyBranch} from '../../../../../utils/branchNames'

import ChangesSinceLastSuccessfulBuild from './ChangesSinceLastSuccessfulBuild'
import type {ContainerProps} from './ChangesSinceLastSuccessfulBuild.types'

import {useAppSelector} from 'src/hooks/react-redux'

function ChangesSinceLastSuccessfulBuildContainer(props: ContainerProps) {
  const {buildTypeId, buildId, changesCount} = props

  const buildStatus = useAppSelector(state => getBuild(state, buildId)?.status ?? 'UNKNOWN')
  const buildState = useAppSelector(state => getBuild(state, buildId)?.state)
  const buildBranchSelector = React.useMemo(makeBuildBranchSelector, [])
  const branch = useAppSelector(state => buildBranchSelector(state, buildId) ?? defaultBranch)

  const isBuildFailed =
    buildState === 'finished' && (buildStatus === 'FAILURE' || buildStatus === 'ERROR')

  const [fetchBuild, {lastSuccessfulBuildId}] =
    restApi.endpoints.getBuildNormalizedAsList.useLazyQuery({
      selectFromResult: ({data}) => ({
        lastSuccessfulBuildId: data?.result[0],
      }),
    })

  const locator = getLastSuccessfulBuildLocator({
    buildTypeId,
    buildId,
    branch,
    inPath: true,
  })

  const hasChanges = changesCount > 0

  useEffect(() => {
    if (isBuildFailed && hasChanges) {
      fetchBuild(getSingleBuildArg(locator))
    }
  }, [fetchBuild, locator, isBuildFailed, hasChanges])

  const currentBuildNumber = useAppSelector(state => getBuild(state, buildId)?.number)

  const lastSuccessfulBuildNumber = useAppSelector(
    state => getBuild(state, lastSuccessfulBuildId)?.number,
  )

  return currentBuildNumber !== undefined &&
    lastSuccessfulBuildNumber !== undefined &&
    isBuildFailed &&
    hasChanges ? (
    <ChangesSinceLastSuccessfulBuild
      branch={stringifyBranch(branch)}
      buildTypeId={buildTypeId}
      currentBuildNumber={currentBuildNumber}
      lastSuccessfulBuildNumber={lastSuccessfulBuildNumber}
    />
  ) : null
}

export default memo(ChangesSinceLastSuccessfulBuildContainer)
